/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import MuiCardActions from '@mui/material/CardActions';
import React from 'react';

import { Colors } from 'src/common/ui/base/Colors';
import { Spacing } from 'src/common/ui/base/Spacing';
import { TypographyStyles } from 'src/common/ui/base/Typography';

type StatusPromptBodyTextProps = {
  text: string | React.ReactNode;
  icon?: React.ElementType;
  iconColor?: string;
};

export const StatusPromptBodyText = ({
  text,
  icon: IconComponent,
  iconColor,
}: StatusPromptBodyTextProps) => {
  return (
    <p
      css={css`
        ${TypographyStyles.body1}
        margin: 0;
        margin-bottom: ${Spacing.Unit1}px;
        display: inline-block;
        text-align: center;
        position: relative;
      `}
    >
      {IconComponent && (
        <IconComponent
          css={css`
            font-size: 18px;
            color: ${iconColor};
            margin-right: 4px;
            vertical-align: baseline;
            position: relative;
            top: 2px;
          `}
        />
      )}
      {text}
    </p>
  );
};

type StatusPromptBodyProps = {
  children: React.ReactNode;
};

export const StatusPromptBody = ({ children }: StatusPromptBodyProps) => {
  return (
    <div
      css={css`
        gap: ${Spacing.Unit3}px;
      `}
    >
      {children}
    </div>
  );
};

type StatusPromptIconProps = {
  icon: React.ElementType;
};

export const StatusPromptIcon = ({ icon: IconComponent }: StatusPromptIconProps) => {
  return (
    <div
      css={css`
        width: 70px;
        height: 70px;
        display: flex;
        align-items: center;
        justify-content: center;
      `}
    >
      <IconComponent />
    </div>
  );
};

type StatusPromptHeadlineProps = {
  headline: string | React.ReactNode;
};

export const StatusPromptHeadline = ({ headline }: StatusPromptHeadlineProps) => {
  return (
    <h2
      css={css`
        ${TypographyStyles.headline2}
        margin: 0;
        padding-left: ${Spacing.Unit5}px;
        padding-right: ${Spacing.Unit5}px;
      `}
    >
      {headline}
    </h2>
  );
};

type StatusPromptActionsProps = {
  children: React.ReactElement;
  variant?: 'low-urgency' | 'high-urgency';
};

export const StatusPromptActions = ({
  children,
  variant = 'low-urgency',
}: StatusPromptActionsProps) => {
  return (
    <MuiCardActions
      css={css`
        display: flex;
        justify-content: center;
        padding: 0;
        background-color: ${variant === 'low-urgency' ? Colors.BlueSpruce : Colors.Peach5};
      `}
    >
      {children}
    </MuiCardActions>
  );
};

type StatusPromptCardProps = {
  variant?: 'low-urgency' | 'high-urgency';
  children: React.ReactNode;
};

export const StatusPromptCard = ({ variant = 'low-urgency', children }: StatusPromptCardProps) => {
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        width: 350px;
        gap: ${Spacing.Unit4}px;
        padding: ${Spacing.Unit5}px;
        background-color: ${variant === 'low-urgency' ? Colors.StormCloud : Colors.Coral1};
      `}
    >
      {children}
    </div>
  );
};
